import { useState, useCallback } from 'react'
import { useAdSupplierGroup } from '@/hooks/useAdSupplierGroups'
import { useAdSuppliers } from '@/hooks/useAdSuppliers'
import { useViewMode } from '@/stores/viewMode'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { DataTable } from './data-table'
import { columns } from './columns'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import { Plus } from 'lucide-react'
import { AssignAdSupplierDialog } from '@/pages/adSupplierGroups/AssignAdSupplierDialog'

export default function AdSupplierGroupSuppliersPage() {
  const { id: groupId } = useViewMode()
  const { adSupplierGroup } = useAdSupplierGroup(groupId)

  // Pagination state
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const pageSize = 10

  // Assign supplier dialog state
  const [assignDialogOpen, setAssignDialogOpen] = useState(false)

  const { adSuppliers, pagination, isLoading, isError, mutate } = useAdSuppliers({
    page,
    pageSize,
    supplier_name: search || undefined,
    adSupplierGroupId: groupId ?? undefined,
  })

  const handleSearch = useCallback((value: string) => {
    setSearch(value)
    setPage(1)
  }, [])

  if (isError) {
    return <div className="text-center text-destructive">載入供應商列表失敗。請稍後再試。</div>
  }

  if (!groupId) {
    return <div className="text-center text-destructive">未選擇廣告商集團</div>
  }

  if (isLoading && !adSuppliers) {
    return (
      <div>
        <Skeleton className="h-8 w-[200px] mb-4" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    )
  }

  const totalPages = pagination?.totalPages ?? 1

  return (
    <div>
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">供應商列表</h2>
          <p className="text-muted-foreground mt-2">
            {adSupplierGroup?.group_name ?? '廣告商集團'} - 所屬供應商管理
          </p>
        </div>
        <Button onClick={() => setAssignDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          指派供應商
        </Button>
      </div>

      <div className="h-4" />

      <Card>
        <CardHeader>
          <CardTitle>所屬供應商</CardTitle>
          <CardDescription>目前集團共有 {pagination?.total ?? 0} 間供應商</CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={adSuppliers ?? []}
            onSearch={handleSearch}
            isLoading={isLoading}
            meta={{ reloadData: mutate }}
          />

          {totalPages > 1 && (
            <div className="mt-4">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      className={page <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>

                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                    .map((p, idx, arr) => {
                      const prevPage = arr[idx - 1]
                      const showEllipsis = prevPage && p - prevPage > 1

                      return (
                        <div key={p} className="flex items-center">
                          {showEllipsis && (
                            <PaginationItem>
                              <span className="px-2">...</span>
                            </PaginationItem>
                          )}
                          <PaginationItem>
                            <PaginationLink
                              onClick={() => setPage(p)}
                              isActive={page === p}
                              className="cursor-pointer"
                            >
                              {p}
                            </PaginationLink>
                          </PaginationItem>
                        </div>
                      )
                    })}

                  <PaginationItem>
                    <PaginationNext
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      className={
                        page >= totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'
                      }
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Assign Supplier Dialog */}
      {groupId && (
        <AssignAdSupplierDialog
          open={assignDialogOpen}
          onOpenChange={setAssignDialogOpen}
          groupId={groupId}
          onSuccess={() => mutate()}
        />
      )}
    </div>
  )
}
